import { useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useWorkflows } from 'src/graphql/entities/workflow/hooks/use-workflows';
import { GET_WORKFLOW_VERSIONS_QUERY } from 'src/graphql/entities/workflow/workflow.operations';
import {
  getWorkflowTriggerType,
  WorkflowTriggerType,
} from 'src/graphql/entities/workflow/workflow.trigger';
import { GetWorkflowVersionsQuery } from 'src/graphql/entities/workflow/workflow.types';
import { QueryVariables } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';

/** A platform workflow that is selectable as an emergency override target. */
export interface PlatformWorkflowOption {
  workflowId: string;
  name: string;
  triggerType: WorkflowTriggerType;
}

export interface UsePlatformWorkflowsByTriggerResult {
  /** Platform workflows with an active version, bucketed by their trigger type. */
  workflowsByTrigger: Record<WorkflowTriggerType, PlatformWorkflowOption[]>;
  loading: boolean;
}

const ACTIVE_PLATFORM_VERSION_FILTERS = [
  { attribute: 'partnerId', operator: '=', value: String(PLATFORM_PARTNER_ID) },
  { attribute: 'isActive', operator: '=', value: 'true' },
];

/**
 * Lists the platform partner's workflows that have an active version, grouped by the
 * trigger type their definition declares.
 *
 * Only active versions are queried: a workflow whose version is deactivated is not
 * resolvable at runtime, so offering it as an override target would promise something the
 * backend silently falls back from. The trigger type still lives inside the definition
 * rather than in a column, so each definition is parsed here to place its workflow.
 */
export function usePlatformWorkflowsByTrigger(): UsePlatformWorkflowsByTriggerResult {
  const { showErrorAlert } = useNotifications();
  const { workflows, loading: workflowsLoading } = useWorkflows(PLATFORM_PARTNER_ID);

  const { data, loading: versionsLoading } = useQuery<
    GetWorkflowVersionsQuery,
    QueryVariables
  >(GET_WORKFLOW_VERSIONS_QUERY, {
    variables: {
      params: { filters: ACTIVE_PLATFORM_VERSION_FILTERS, includeDeleted: false },
    },
    onError: (fetchError) => {
      console.error(fetchError);
      showErrorAlert('Failed to load platform workflows. Please try again.');
    },
  });

  const workflowsByTrigger = useMemo(() => {
    const nameByWorkflowId = new Map(
      workflows.map((workflow) => [workflow.workflowId, workflow.name]),
    );
    // Spelled out rather than derived, so adding a trigger type is a compile error here
    // instead of an empty select nobody notices.
    const grouped: Record<WorkflowTriggerType, PlatformWorkflowOption[]> = {
      [WorkflowTriggerType.InboundPhoneCall]: [],
      [WorkflowTriggerType.InboundSms]: [],
    };

    (data?.getWorkflowVersion?.records ?? []).forEach((version) => {
      const triggerType = getWorkflowTriggerType(version.definition);
      const name = version.workflowId ? nameByWorkflowId.get(version.workflowId) : undefined;

      if (!triggerType || !name || !version.workflowId) return;

      const bucket = grouped[triggerType];
      // A workflow with several active versions must still appear once.
      if (bucket.some((option) => option.workflowId === version.workflowId)) return;

      bucket.push({ workflowId: version.workflowId, name, triggerType });
    });

    Object.values(grouped).forEach((bucket) =>
      bucket.sort((a, b) => a.name.localeCompare(b.name)),
    );

    return grouped;
  }, [data?.getWorkflowVersion?.records, workflows]);

  return { workflowsByTrigger, loading: workflowsLoading || versionsLoading };
}
